import { CoachDataDB, CoachMessageDB, DataDB, FavoriteDB, GameStateDB, ModuleStateDB, SectionDataDB, SelfObservationDayDB, UserDB, ConfigDB, ConfigType } from 'src/data/datatypes';
import PouchDb from 'pouchdb';
PouchDb.plugin(require('pouchdb-upsert'));

import _ from 'underscore';
import { Buffer } from 'buffer';
import { isIosDevice, isMobileDevice } from '../CordovaService/NetworkConnectionService';
import { AudioService } from '../CordovaService/AudioService';
import { DB_URL, EventStreamDB, ModuleDataDB, PageStateDB, Userdata, UserEvent } from '@app/common';
import { getStoredLogin } from '../AuthStorageService';
import { dispatchFatalDbError, resetFatalDbErrorDispatch } from '../DbFatalErrorService';

const parsedDbUrl = new URL(DB_URL);
const normalizedPath = parsedDbUrl.pathname.replace(/\/$/, '');
export const SRV_ADDR = `${parsedDbUrl.host}${normalizedPath}`;
export const PROTOCOL = parsedDbUrl.protocol.replace(':', '');

export const getApiBaseUrl = (): string => {
    return SRV_ADDR.startsWith('localhost')
        ? `${PROTOCOL}://localhost:3001/api`
        : `${PROTOCOL}://${SRV_ADDR.replace(/\/couchdb$/, '/api')}`;
};

const syncDesignObject: any = {
    _id: '_design/sync',
    filters: {
        data: ((doc: any, req: any) => {
            return doc._deleted !== true &&
                (
                    doc._id === '_design/sync' ||
                    doc._id === 'userdata' ||
                    doc._id.indexOf('itemstate_') === 0 ||
                    doc._id.indexOf('modulestate_') === 0
                );
        }).toString()
    }
};

let userDB: UserDB;
let dataDB: DataDB;
let remoteFileDB: PouchDB.Database;
let configDb: ConfigDB;
let liveUserSync: { cancel: () => void } | undefined;
let liveDataReplication: { cancel: () => void } | undefined;
let liveConfigReplication: { cancel: () => void } | undefined;
let unauthorizedSyncErrorDispatched = false;

const makeBasicAuthHeader = (username: string, password: string): string => {
    return `Basic ${Buffer.from(`${username}:${password}`).toString('base64')}`;
};

const createAuthenticatedRemoteDb = (
    dbName: string,
    username: string,
    password: string
): PouchDB.Database => {
    const authHeader = makeBasicAuthHeader(username, password);
    return new PouchDb(`${PROTOCOL}://${SRV_ADDR}/${dbName}`, {
        fetch: (url: RequestInfo | URL, opts?: RequestInit) => {
            const headers = new Headers(opts?.headers);
            headers.set('Authorization', authHeader);
            return fetch(url, {
                ...opts,
                headers,
            });
        }
    });
};

const createRemoteFileDb = (studyId: string | undefined): PouchDB.Database => {
    const storedLogin = getStoredLogin();
    return createAuthenticatedRemoteDb(`${studyId}-files`, storedLogin?.username || '', storedLogin?.password || '');
};

export const cancelLiveSyncs = (): void => {
    liveUserSync?.cancel();
    liveDataReplication?.cancel();
    liveConfigReplication?.cancel();
    liveUserSync = undefined;
    liveDataReplication = undefined;
    liveConfigReplication = undefined;
};

export const initLocalDbs = () => {
    if (isIosDevice()) {    // use sqlite as local db
        PouchDb.plugin(require('pouchdb-adapter-cordova-sqlite'));
        userDB = new PouchDb('UserDB', {
            adapter: 'cordova-sqlite',
            iosDatabaseLocation: 'default',
            androidDatabaseImplementation: 2,
        });
        console.log('Creating DBs for ios');
        dataDB = new PouchDb('DataDB', {
            adapter: 'cordova-sqlite',
            iosDatabaseLocation: 'default',
            androidDatabaseImplementation: 2
        });
        configDb = new PouchDb('ConfigDB', {
            adapter: 'cordova-sqlite',
            iosDatabaseLocation: 'default',
            androidDatabaseImplementation: 2
        });
    } else if (isMobileDevice()) {
        PouchDb.plugin(require('pouchdb-adapter-cordova-sqlite'));
        userDB = new PouchDb('UserDB', {
            adapter: 'cordova-sqlite',
            iosDatabaseLocation: 'default',
            androidDatabaseImplementation: 2,
        });
        console.log('Creating DBs for android');
        dataDB = new PouchDb('DataDB', {
            adapter: 'cordova-sqlite',
            iosDatabaseLocation: 'default',
            androidDatabaseImplementation: 2
        });
        configDb = new PouchDb('ConfigDB', {
            adapter: 'cordova-sqlite',
            iosDatabaseLocation: 'default',
            androidDatabaseImplementation: 2
        });
    } else {    // use indexedDB for browser
        userDB = new PouchDb('UserDB');
        dataDB = new PouchDb('DataDB');
        configDb = new PouchDb('ConfigDB');
    }
};

export const getTimestampAsString = (): string => {
    return JSON.stringify(new Date()).replace('"', '').replace('"', '');
};

const withTimestamp = (object: any): any => {
    return {
        ...object,
        last_modified: getTimestampAsString()
    };
};

const newDocHasChanges = (oldDoc: any, newDoc: any): boolean => {
    return !Object.keys(newDoc).every(key => oldDoc[key] === newDoc[key]);
};

export enum ConnectionState {
    DISCONNECTED,
    CONNECTING,
    SYNCHRONIZING,
    CONNECTION_ESTABLISHED,
    CONNECTION_FAILED,
    PASSWORD_CHANGED
}

const failInitialConnection = (code: string, error: unknown): ConnectionState => {
    cancelLiveSyncs();
    dispatchFatalDbError(code, error);
    return ConnectionState.CONNECTION_FAILED;
};

const isUnauthorizedSyncError = (error: any): boolean => {
    return error?.status === 401 || error?.status === 403;
};

const handleUnauthorizedSyncError = (): void => {
    if (unauthorizedSyncErrorDispatched) {
        return;
    }

    unauthorizedSyncErrorDispatched = true;
    cancelLiveSyncs();
    document.dispatchEvent(new CustomEvent('onUserUnauthorized'));
};

export async function* connect(username: string, password: string) {
    cancelLiveSyncs();
    unauthorizedSyncErrorDispatched = false;
    resetFatalDbErrorDispatch();
    yield ConnectionState.CONNECTING;

    let usernameHex;
    try {
        usernameHex = Buffer.from(username, 'utf-8').toString('hex')
    } catch (error) {
        return failInitialConnection('DB_LOGIN_USERNAME_ENCODE_FAILED', error);
    }

    const remoteUserDB: UserDB = createAuthenticatedRemoteDb(`userdb-${usernameHex}`, username, password) as UserDB;
    const remoteConfigDB: ConfigDB = createAuthenticatedRemoteDb('study-configs', username, password) as ConfigDB;

    // check if design object for sync exists
    try {
        await remoteUserDB.get('_design/sync');
    } catch (err) {
        if (err.name === 'not_found') {
            try {
                await remoteUserDB.put(syncDesignObject);
            } catch (error) {
                return failInitialConnection('DB_LOGIN_SYNC_DESIGN_DOC_FAILED', error);
            }
        } else {
            return failInitialConnection('DB_LOGIN_SYNC_DESIGN_DOC_FAILED', err);
        }
    }

    // one-time sync
    yield ConnectionState.SYNCHRONIZING;
    try {
        await new Promise<void>((resolve, reject) => {
            userDB.replicate.from(remoteUserDB, { live: false, retry: true, batch_size: 10 })
                .on('change', info => {
                    // TODO: progress update
                    console.log('user db sync: change', info);
                }).on('paused', info => {
                    // TODO: internet connection might be disabled => promise never returns
                    console.log('user db sync: paused', info);
                }).on('complete', info => {
                    console.log('user db sync: complete', info);
                    resolve();
                }).on('error', err => {
                    console.log('user db sync: error', err);
                    reject(err);
                }).on('error', console.error);
        });
    } catch (error) {
        return failInitialConnection('DB_LOGIN_USERDB_SYNC_FAILED', error);
    }

    // get userdata to get studyId, to get correct modules and config data
    let user: Userdata;
    try {
        user = await userDB.get('userdata');
    } catch (error) {
        return failInitialConnection('DB_LOGIN_USERDATA_LOAD_FAILED', error);
    }
    const studyId = user.studyId;

    const remoteDataDB: DataDB = createAuthenticatedRemoteDb(`${studyId}-modules`, username, password) as DataDB;
    try {
        await new Promise<void>((resolve, reject) => {
            dataDB.replicate.from(remoteDataDB, { live: false, retry: true, batch_size: 10 })
                .on('change', info => {
                    // TODO: progress update
                    console.log('data db sync: change', info);
                }).on('paused', info => {
                    // TODO: internet connection might be disabled => promise never returns
                    console.log('data db sync: paused', info);
                }).on('complete', info => {
                    console.log('data db sync: complete', info);
                    resolve();
                }).on('error', err => {
                    console.log('data db sync: error', err);
                    reject(err);
                });
        });
    } catch (error) {
        return failInitialConnection('DB_LOGIN_MODULEDB_SYNC_FAILED', error);
    }
    try {
        await new Promise<void>((resolve, reject) => {
            configDb.replicate.from(remoteConfigDB, { live: false, retry: true, batch_size: 10 })
                .on('change', info => {
                    // TODO: progress update
                    console.log('config db sync: change', info);
                }).on('paused', info => {
                    // TODO: internet connection might be disabled => promise never returns
                    console.log('config db sync: paused', info);
                }).on('complete', info => {
                    console.log('config db sync: complete', info);
                    resolve();
                }).on('error', err => {
                    console.log('config db sync: error', err);
                    reject(err);
                });
        });
    } catch (error) {
        return failInitialConnection('DB_LOGIN_CONFIGDB_SYNC_FAILED', error);
    }

    // get user config
    let config: ConfigType | null = null;
    if (studyId) { // it doesn't hurt to still keep studyId in userdata for web/mobile client user, since study participants can only belong to one tenant
        try {
            config = await configDb.get(studyId);
        } catch (error) {
            return failInitialConnection('DB_LOGIN_CONFIG_LOAD_FAILED', error);
        }
    }
    yield ConnectionState.CONNECTION_ESTABLISHED;

    // activate continuous sync
    liveUserSync = userDB.sync(remoteUserDB, { live: true, retry: true }) // read-write
        .on('change', info => {
            console.log('user db continuous sync: change', info);
            if (info.direction === 'pull') {
                const userdataDoc = info.change.docs.find(doc => doc._id === 'userdata');
                if (userdataDoc !== undefined) {
                    console.log('detected change in userdata');
                    const userdataChanged = new CustomEvent<Userdata>('onUserdataChange', {
                        detail: userdataDoc as any
                    });
                    document.dispatchEvent(userdataChanged);
                }
            }
        }).on('paused', info => {
            console.log('user db continuous sync: paused', info);
        }).on('error', (err: any) => {
            console.log('user db continuous sync: error', err);
            if (isUnauthorizedSyncError(err)) {
                handleUnauthorizedSyncError();
            }
        }).on('error', console.error);
    liveDataReplication = dataDB.replicate.from(remoteDataDB, { live: true, retry: true }) // readonly
        .on('change', info => {
            console.log('data db continuous sync: change', info);
        }).on('paused', info => {
            console.log('data db continuous sync: paused', info);
        }).on('error', (err: any) => {
            console.log('data db continuous sync: error', err);
            if (isUnauthorizedSyncError(err)) {
                handleUnauthorizedSyncError();
            }
        });
    liveConfigReplication = configDb.replicate.from(remoteConfigDB, { live: true, retry: true }) // readonly
        .on('change', info => {
            console.log('config db continuous sync: change', info);
        }).on('paused', info => {
            console.log('config db continuous sync: paused', info);
        }).on('error', (err: any) => {
            console.log('config db continuous sync: error', err);
            if (isUnauthorizedSyncError(err)) {
                handleUnauthorizedSyncError();
            }
        });   
    return { user, config };
}

// CONFIG

async function getConfigById(id: string) {
    return configDb.get<ConfigType>(id);
}

export async function getConfig() {
    try {
        const userDoc = await userDB.get<Userdata>('userdata');
        const studyId = userDoc.studyId;
        if (studyId) {
            const config = await getConfigById(studyId);
            return config;
        }
        return null;
    } catch (error) {
        console.error(error);
        return null;
    }
}

// DATA

export async function getSection(sectionId: `section_${string}`) {
    return dataDB.get<SectionDataDB>(sectionId);
}

export async function getModule(moduleId: `module_${string}`) {
    return dataDB.get<ModuleDataDB>(moduleId);
}

export async function getAllModules() {
    const dbResult = await dataDB.allDocs<ModuleDataDB>({
        include_docs: true,
        startkey: 'module_',
        endkey: 'module_\uffff'
    });
    return dbResult.rows.map(row => row.doc) as ModuleDataDB[];
}

export async function getCoach(id: `coach_${string}`): Promise<CoachDataDB | null> {
    try {
        return await dataDB.get<CoachDataDB>(id);
    } catch (e) {
        if (e.status === 404) {
            return null;
        }
        throw e;
    }
}

export async function getAllCoaches() {
    const dbResult = await dataDB.allDocs<CoachDataDB>({
        include_docs: true,
        startkey: 'coach_',
        endkey: 'coach_\uffff'
    });
    return dbResult.rows.map(row => row.doc) as CoachDataDB[];
}

export async function getCoachMessage(id: `coachmessage_${string}`): Promise<CoachMessageDB | null> {
    try {
        return await dataDB.get<CoachMessageDB>(id);
    } catch (e) {
        if (e.status === 404) {
            return null;
        }
        throw e;
    }
}

// USER

export async function saveUser(user: Userdata) {
    let userDoc = await userDB.get('userdata');
    await userDB.put({
        ...user,
        _rev: userDoc._rev
    });
    // FIXME: get new _rev from put?
    userDoc = await userDB.get('userdata');
    user._rev = userDoc._rev;
}

export async function resetUserDb() {
    const dbResult = await userDB.allDocs();
    console.log(dbResult);

    for (const row of dbResult.rows) {
        if (!row.id.startsWith('_') && row.id !== 'userdata') {
            console.log('removing id ' + row.id);
            const doc = await userDB.get(row.id);
            await userDB.remove(doc);
            console.log('removing id ' + row.id + ' done');
        }
    }
    console.log('resetUser is done');
}

// PAGE

export async function getPageState(id: string): Promise<PageStateDB | undefined> {
    try {
        return await userDB.get<PageStateDB>(id);
    } catch (e) {
        if (e.status === 404) {
            return undefined;
        }
        throw e;
    }
}

export async function savePageState(
    state: PageStateDB,
    blobs?: Record<string, Blob | null>
): Promise<void> {
    const hasBlobs = !!blobs && Object.keys(blobs).length > 0;

    // Check if should save
    const audioInputElementKey = Object.keys(state.values).find(element => typeof state.values[element] === 'string' && state.values[element].startsWith('__audio:attached'));
    if (!!audioInputElementKey && !hasBlobs) {
        console.warn('[savePageState] blobs not received yet, returning...');
        return;
    }

    try {
        (userDB as any).upsert(state._id, function (_doc: any) {
            let isNew = false;
            let doc = { ..._doc } as PageStateDB;
            // console.log('[savePageState] doc: ', doc);

            if (!doc._id) {
                doc = { _id: state._id, values: {}, _attachments: {} };
                isNew = true;
            }

            const updatedValues = { ...doc.values, ...state.values };
            let updatedAttachments = { ...doc._attachments };
            let hasChanges = false;

            // Check if values changed
            for (const key of Object.keys(updatedValues)) {
                if (!doc.values[key] || updatedValues[key] !== doc.values[key]) {
                    hasChanges = true;
                    break;
                }
            }

            // console.log('[savePageState] blobs: ', blobs);

            // Handle blobs
            if (!_.isEmpty(blobs)) {
                for (const [fieldKey, blob] of Object.entries(blobs as Record<string, Blob | null>)) {
                    const audioService = new AudioService();
                    const extension = audioService.getSupportedFileExtension();
                    const filename = `${fieldKey}.${extension}`;
                    console.log('[savePageState] blob: ', blob);

                    if (blob === null) {
                        console.log('[sagePageState] blob is null, removing entry from _attachments...');
                        delete updatedAttachments[filename];
                    } else {
                        console.log('[savePageState] filename: ', filename);
                        const contentType = blob.type || 'application/octet-stream';

                        updatedAttachments[filename] = {
                            content_type: contentType,
                            data: blob,
                        };
                        hasChanges = true;
                    }
                }
            } else {
                updatedAttachments = {};
            }

            if (hasChanges || isNew) {
                let newDoc = {
                    ...doc,
                    ...state,
                    values: updatedValues
                };

                // console.log('updatedAttachments: ', updatedAttachments);
                newDoc._attachments = updatedAttachments;

                const newDocWithNewTimestamp = withTimestamp(newDoc);
                return newDocWithNewTimestamp;
            }
        }).then(function (res: any) {
            // success
            // console.log(res);
        }).catch(function (err: any) {
            // error
            console.error('Failed to save page state:', err);
        });

    } catch (err: any) {
        console.error('Failed to save page state:', err);
        throw err;
    }
}

// ATTACHMENTS
export async function getAttachmentById(docId: string, attachmentId: string) {
    return await userDB.getAttachment(docId, attachmentId);
}

export async function deleteAttachmentById(docId: string, attachmentId: string, rev: string) {
    const result = await userDB.removeAttachment(docId, attachmentId, rev);
    console.log('[deleteAttachmentById] result: ', result);
    return result;
}

// GAME

export async function getGameState(id: string): Promise<GameStateDB | undefined> {
    try {
        return await userDB.get<GameStateDB>(id);
    } catch (e) {
        if (e.status === 404) {
            return undefined;
        }
        throw e;
    }
}

export async function saveGameState(state: GameStateDB): Promise<void> {
    // don't save empty objects
    if (Object.keys(state.state).length === 0) {
        return;
    }
    try {
        // fetch the document, then update it.
        // necessary to avoid revision conflicts.
        const doc = await userDB.get<GameStateDB>(state._id);

        // only save if changes exist
        if (!_.isEqual(doc.state, state.state)) {
            await userDB.put(withTimestamp({
                ...state,
                _rev: doc._rev
            }));
        }
    } catch (e) {
        // create doc if it doesn't exist already
        if (e.status === 404) {
            await userDB.put(withTimestamp({
                ...state
            }));
        } else {
            throw e;
        }
    }
}

// MODULE STATE

export function getInitialModuleState(id: `module_${string}`): ModuleStateDB {
    return {
        _id: id,
        finished: {},
        modifiers: [],
        elementValues: {}
    };
}

export async function getModuleState(id: `module_${string}`): Promise<ModuleStateDB | undefined> {
    try {
        return await userDB.get<ModuleStateDB>(id);
    } catch (e) {
        if (e.status === 404) {
            return undefined;
        }
        throw e;
    }
}

export async function checkTutorialModulesDone(currentFinishedModule: `module_${string}`): Promise<boolean> {
    if (currentFinishedModule === 'module_01_tutorial') {
        let otherModuleState = await getModuleState('module_02_einfuehrung');
        if (otherModuleState?.finished.end) {
            return true;
        }
        otherModuleState = await getModuleState('module_02_einfuehrung_partner');
        if (otherModuleState?.finished.end) {
            return true;
        }
        return false;
    } else if (currentFinishedModule === 'module_02_einfuehrung' || currentFinishedModule === 'module_02_einfuehrung_partner') {
        let otherModuleState = await getModuleState('module_01_tutorial');
        if (otherModuleState?.finished.end) {
            return true;
        }
        return false;
    }
    return true;
}

export async function getAllModuleStates(): Promise<ModuleStateDB[]> {
    try {
        const dbResult = await userDB.allDocs<ModuleStateDB>({
            include_docs: true,
            startkey: 'module_',
            endkey: 'module_\uffff'
        });
        return dbResult.rows.map(row => row.doc) as ModuleStateDB[];
    } catch (e) {
        console.log('Something went wrong fetching module states', e);
        throw new Error('Something went wrong fetching module states');
    }
}

export async function saveModuleState(state: ModuleStateDB): Promise<void> {
    try {
        // fetch the document, then update it.
        // necessary to avoid revision conflicts.
        if (state._id === null) {
            throw new Error('404');
        }
        const doc = await userDB.get<ModuleStateDB>(state._id);

        // only save if changes exist
        if (
            !_.isEqual(state.modifiers, doc.modifiers) ||
            !_.isEqual(state.finished, doc.finished) ||
            !_.isEqual(state.elementValues, doc.elementValues)
        ) {
            await userDB.put(withTimestamp({
                ...state,
                _rev: doc._rev
            }));
        }
    } catch (e) {
        // create doc if it doesn't exist already
        if (e.status === 404 || e === '404') {
            await userDB.put(withTimestamp(state));
        } else {
            throw e;
        }
    }
}

export const getUserdataDocs = async (topic?: string): Promise<PageStateDB[]> => {
    const options: any = {
        include_docs: true,
    };
    if (topic) {
        options.startkey = topic;
        options.endkey = topic + '\uffff';
    }

    const res = await userDB.allDocs(options);
    return res.rows.map(row => row.doc).filter(doc => {
        if (!doc) {
            return false;
        }
        return !['_design/roleValidation', '_design/sync'].includes(doc._id);
    }) as PageStateDB[];
}

// FAVORITES

export async function getFavorites(): Promise<FavoriteDB[]> {
    try {
        const dbResult = await userDB.allDocs<FavoriteDB>({
            include_docs: true,
            startkey: 'fav_',
            endkey: 'fav_\uffff'
        });
        return dbResult.rows.map(row => row.doc) as FavoriteDB[];
    } catch (e) {
        console.log('Something went wrong fetching favorites', e);
        throw new Error('Something went wrong fetching favorites');
    }
}

export async function getFavorite(id: string): Promise<FavoriteDB | undefined> {
    try {
        return await userDB.get<FavoriteDB>(id);
    } catch (e) {
        if (e.status === 404) {
            return undefined;
        } else {
            throw e;
        }
    }
}

export async function createOrUpdateFavorite(fav: FavoriteDB) {
    try {
        const doc = await userDB.get(fav._id);
        await userDB.put(withTimestamp({
            ...fav,
            _rev: doc._rev
        }));
    } catch (e) {
        if (e.status === 404) {
            await userDB.put(withTimestamp(fav));
        } else {
            throw e;
        }
    }
}

export async function deleteFavorite(id: `fav_${string}`) {
    const doc = await userDB.get(id);
    await userDB.remove(doc);
}

// SELF-OBSERVATION

// from and to should be date strings of the format YYYY-MM-DD
export async function getSelfObservationDays(from: string, to: string): Promise<SelfObservationDayDB[]> {
    try {
        const dbResult = await userDB.allDocs<SelfObservationDayDB>({
            include_docs: true,
            startkey: `selfobservation_${from}`,
            endkey: `selfobservation_${to}`
        });
        return dbResult.rows.map(row => row.doc) as SelfObservationDayDB[];
    } catch (e) {
        console.log('Something went wrong fetching self-observation-days', e);
        throw new Error('Something went wrong fetching self-observation-days');
    }
}

export async function getSelfObservationDay(id: string): Promise<SelfObservationDayDB | undefined> {
    try {
        return await userDB.get<SelfObservationDayDB>(id);
    } catch (e) {
        if (e.status === 404) {
            return undefined;
        } else {
            throw e;
        }
    }
}

export async function createOrUpdateSelfObservationDay(day: SelfObservationDayDB) {
    try {
        const doc = await userDB.get(day._id);
        if (newDocHasChanges(doc, day)) {
            await userDB.put(withTimestamp({
                ...day,
                _rev: doc._rev
            }));
        }
    } catch (e) {
        if (e.status === 404) {
            await userDB.put(withTimestamp({
                ...day
            }));
        } else {
            throw e;
        }
    }
}

// STATIC-DATA

export async function getFileRemote(id: string, studyId: string | undefined) {
    remoteFileDB = createRemoteFileDb(studyId);
    if (remoteFileDB) {
        const doc = await remoteFileDB.get(id, { attachments: true });
        if (doc._attachments) {
            return doc;
        }
    }
    return null;
}

export async function getAllFilesRemote_OLD() {
    const dbResult = await remoteFileDB.allDocs();
    return dbResult.rows;
}

export async function getAllFilesRemote(studyId: string | undefined) {
    remoteFileDB = createRemoteFileDb(studyId);
    const dbResult = await remoteFileDB.allDocs({ include_docs: true });

    const filteredRows = dbResult.rows.filter((row: any) =>
        row.doc && (
            !row.doc.studyId || row.doc.studyId === studyId
        )
    );

    return filteredRows;
}

// EVENT-STREAM

export async function saveEvents(...eventsToSave: UserEvent[]) {
    try {
        const eventDoc = await userDB.get<EventStreamDB>('eventstream');
        const newEvents = [...eventDoc.events, ...eventsToSave];
        await userDB.put({
            ...eventDoc,
            events: newEvents
        });
    } catch (e) {
        if (e.status === 404) {
            await userDB.put({
                _id: 'eventstream',
                events: [...eventsToSave]
            });
        } else {
            console.log(e);
            throw e;
        }
    }
}

export async function getAllEvents(): Promise<UserEvent[]> {
    try {
        const eventDoc = await userDB.get<EventStreamDB>('eventstream');
        return eventDoc.events;
    } catch (e) {
        if (e.status === 404) {
            return [];
        } else {
            console.log(e);
            throw e;
        }
    }
}